import React from 'react';
import ReactMarkdown from 'react-markdown';

interface EnhancedMarkdownProps {
  content: string;
  className?: string;
}

const EnhancedMarkdown: React.FC<EnhancedMarkdownProps> = ({ content, className = '' }) => {
  // Parse and enhance the content
  const processContent = (text: string) => {
    // Replace checkmarks with enhanced checkmark components
    return text
      .replace(/✓/g, '✅')
      .replace(/✔/g, '✅')
      .replace(/☑/g, '✅');
  };

  const processedContent = processContent(content);

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        components={{
          // Headings
          h1: ({ node, ...props }) => (
            <h1 className="text-xl font-bold mb-3 mt-4 gradient-text" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="text-lg font-bold mb-2 mt-3 text-[#8b3dff]" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="text-base font-semibold mb-2 mt-3 text-[#a855f7]" {...props} />
          ),

          // Paragraphs
          p: ({ node, ...props }) => (
            <p className="mb-3 last:mb-0 leading-relaxed text-gray-100" {...props} />
          ),

          // Strong/Bold text
          strong: ({ node, children, ...props }) => {
            const text = String(children);
            // Check if it's a field label (ends with colon)
            if (text.endsWith(':')) {
              return (
                <strong className="inline-block font-bold text-[#8b3dff] bg-[#8b3dff]/10 px-2 py-0.5 rounded border border-[#8b3dff]/20 mr-1" {...props}>
                  {children}
                </strong>
              );
            }
            return (
              <strong className="font-bold text-white bg-[#8b3dff]/15 px-1.5 py-0.5 rounded" {...props}>
                {children}
              </strong>
            );
          },

          // Inline code
          code: ({ node, className, children, ...props }) => {
  const isInline = !className || !className.includes('language-');
  
  if (isInline) {
    return (
      <code
        className="px-2 py-1 bg-[#1e293b] border border-[#334155] rounded text-sm font-mono text-[#a855f7]"
        {...props}
      >
        {children}
      </code>
    );
  }
  // Code block
  return <code className={className} {...props}>{children}</code>;
},

          // Code blocks
          pre: ({ node, ...props }) => (
            <pre className="bg-[#0a0f1e] border border-[#334155] rounded-lg p-4 overflow-x-auto my-3 text-sm" {...props} />
          ),

          // Lists
          ul: ({ node, ...props }) => (
            <ul className="space-y-2.5 my-3" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="space-y-2.5 my-3" {...props} />
          ),

          // List items with enhanced checkmark rendering
          li: ({ node, children, ...props }) => {
            const content = String(children);
            const hasCheckmark = content.includes('✅') || content.includes('✓') || content.includes('✔') || content.includes('☑');
            
            if (hasCheckmark) {
              // Remove checkmark from content
              const cleanContent = content.replace(/[✅✓✔☑]/g, '').trim();
              
              return (
                <li className="flex items-start gap-3 group" {...props}>
                  <span className="flex-shrink-0 w-5 h-5 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-400 mt-0.5 group-hover:bg-emerald-500/25 transition-colors">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="flex-1 text-gray-100">{cleanContent || children}</span>
                </li>
              );
            }
            
            // Regular list item with bullet point
            return (
              <li className="flex items-start gap-3" {...props}>
                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#8b3dff] mt-2"></span>
                <span className="flex-1 text-gray-100">{children}</span>
              </li>
            );
          },

          // Blockquote
          blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-4 border-[#8b3dff] bg-[#8b3dff]/5 pl-4 pr-4 py-2 italic text-gray-300 my-3 rounded-r" {...props} />
          ),

          // Horizontal rule
          hr: ({ node, ...props }) => (
            <hr className="border-0 h-px bg-gradient-to-r from-transparent via-[#8b3dff]/30 to-transparent my-4" {...props} />
          ),

          // Links
          a: ({ node, ...props }) => (
            <a 
              className="text-[#8b3dff] hover:text-[#7a35e6] underline decoration-[#8b3dff]/40 hover:decoration-[#8b3dff] underline-offset-2 transition-all" 
              target="_blank"
              rel="noopener noreferrer"
              {...props} 
            />
          ),

          // Tables
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-3">
              <table className="w-full border-collapse" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead className="bg-[#8b3dff]/15" {...props} />
          ),
          th: ({ node, ...props }) => (
            <th className="px-4 py-2 text-left font-semibold border border-[#334155] text-[#8b3dff]" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="px-4 py-2 border border-[#334155] text-gray-100" {...props} />
          ),
          tr: ({ node, ...props }) => (
            <tr className="hover:bg-[#1e293b]/30 transition-colors" {...props} />
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default EnhancedMarkdown;